/**
 * packages/dados/src/mapeamento.ts — fronteira BANCO ⇄ MOTOR, nos dois sentidos.
 *
 * O motor consome `Empreendimento` + `Lev` (engine/tipos.ts). O banco guarda a mesma
 * informação espalhada em nove tabelas (`app.empreendimento`, `app.cenario`,
 * `app.cenario_mes`, `app.unidade`, `app.vaga`, `app.unidade_vaga`,
 * `app.cenario_obra_servico`, `app.cenario_obra_mes`, `app.cenario_trava`).
 *
 * REGRA DESTE ARQUIVO: `paraMotor(paraBanco(x))` devolve `x` — **idêntico**, incluindo
 * presença de chave, `null` e string vazia. Provado nos 64 casos de
 * `tests/golden/referencia.json` por `tests/mapeamento.test.ts`.
 *
 * ---------------------------------------------------------------------------
 * ARMADILHAS DO DOMÍNIO (todas preservadas de propósito — não "consertar")
 * ---------------------------------------------------------------------------
 * 1. `u.m2` tem DUAS semânticas e mora numa coluna só (`app.unidade.fracao_ideal`):
 *    no lado `det` é METRAGEM (40, 50, 60…), no lado `dem` é FRAÇÃO IDEAL (0..1).
 *    O discriminador é `app.lado`. Aqui não se normaliza nem se valida faixa.
 * 2. Vínculo unidade↔vaga avulsa é N:N em `app.unidade_vaga` e vira `u.vagas[]` (ids)
 *    no motor. A ordem de `u.vagas` é reconstruída pela `ordem` da vaga.
 * 3. Datas trafegam como string ISO `YYYY-MM-DD` e vão LITERAIS para a coluna `date`.
 *    Este arquivo NUNCA constrói `Date` — é o que evita o deslocamento de fuso.
 *    `''` (vazio, do protótipo) ⇄ `NULL`.
 * 4. Campos que o protótipo mantém por compatibilidade e que parecem mortos
 *    (`b.unidadesDet`, `lev.pctFinPess`, `lev.pls0Data`) sobrevivem à ida e volta.
 *
 * ---------------------------------------------------------------------------
 * O QUE NÃO TEM COLUNA — e por isso viaja em `extras.motor` (a válvula do R-10)
 * ---------------------------------------------------------------------------
 * `extras` é, por decisão de plano, "válvula para campo que o motor NÃO lê". Nada do
 * que o motor lê é guardado ali: o VALOR está sempre na coluna. O que vai para
 * `extras.motor` é (a) campo do protótipo sem coluna nenhuma e (b) a FIDELIDADE que
 * o tipo da coluna não expressa (chave ausente × presente, `null` × 0). Lista fechada,
 * validada em teste — nada além disto entra:
 *
 *   cenario.extras.motor        = { ausentes?, nulos?, id?, pctFinPess?, pls0Data?,
 *                                   linhaCredito?, travadas?, vagas?, emp? }
 *   cenario.extras.motor.emp    = { ausentes?, nulos?, id?, unidadesDet? }   ← a fidelidade de `b`
 *
 *   CADASTRO POR CENÁRIO (0032, revisão de 11–12/10/2026, CAL-1): o cadastro do empreendimento
 *   (`b` do motor: programa, rating, VGV, unidades, CTO, despesas, terreno, permutadas, LGC
 *   disponível e PJ limite) mora nas colunas de `app.cenario`, e a fidelidade dele em
 *   `cenario.extras.motor.emp` — UM namespace só, porque `unidadesDet` existe em `b` e em `lev`.
 *   De `app.empreendimento` o mapeamento lê só a IDENTIDADE (id e nome); as colunas antigas do
 *   cadastro e o `extras` dele ficaram congelados e não são lidos nem escritos. O objeto
 *   `{empreendimento, lev}` que o motor recebe NÃO mudou.
 *
 *   • `ausentes` — chaves OPCIONAIS que não existiam no objeto do motor. Sem isto,
 *     `obraServ: {}` e `obraServ` ausente seriam a mesma coisa (61 dos 64 goldens não
 *     têm a chave).
 *   • `nulos` — chaves cujo valor era exatamente `null`. As colunas são `NOT NULL`;
 *     o motor lê tudo com `||0`, então a coluna recebe 0 e a NULIDADE fica registrada.
 *     Único caso do golden: S20b ("Zerado (NULL)"), 10 campos em `b` e 8 em `lev`.
 *   • `id` — identidade do motor que não é UUID (ex.: `'tramontto'`). Quando `b.id` já
 *     é UUID, ele VIRA o `id` da linha e `extras` fica vazio (caminho do produto).
 *   • `vagas[<id da linha>] = { id?, tipo? }` — o token `vg<N>` do protótipo (que se
 *     repete entre os lados por causa do `espelharDados`, logo não pode ser PK) e o
 *     nome do tipo quando estiver fora de `app.ref_vaga_tipo`.
 *
 * ---------------------------------------------------------------------------
 * PREMISSAS ASSUMIDAS (verdadeiras nos 64 goldens; ver relatório do lote)
 * ---------------------------------------------------------------------------
 * A1 `lev.mesInicial` é `'YYYY-MM'` (input type=month). A coluna é `date` com
 *    CHECK dia 1, então grava-se `YYYY-MM-01` e devolve-se `slice(0,7)`.
 * A2 `dataRegistro` só existe no lado `det` (CHECK `un_data_registro_lado`), que é
 *    exatamente o que a fábrica de unidade do v67 faz.
 * A3 `valorSolicitadoAval` e `dataAvaliacao` andam juntos (CHECK `un_aval_coerente`).
 * A4 Precisão: as colunas são `numeric(18,2)` (R$), `numeric(12,6)` (% e obra) e
 *    `numeric(18,9)` (fração). Este mapeamento NÃO quantiza — quem quantiza é o
 *    Postgres, no INSERT. Ver o teste "quantização" para a lista congelada.
 * A5 Unidade, vaga e mês carregam o conjunto de chaves da fábrica do v67; não há
 *    coluna `extras` nessas tabelas para registrar desvio por linha.
 */

import type { Database, Json } from './banco.tipos';
import type { Empreendimento, Lev, Mes, Unidade, Vaga } from '../../../engine/tipos';

/* ============================================================================
 * 1. Tipos de linha — derivados dos tipos gerados do Supabase.
 *
 * `Insercao<T> & Obrigatorias<T, K>`: aceita ser inserido (sem exigir `criado_em`,
 * `versao`, …) e GARANTE, no tipo, que toda coluna que `paraMotor` lê está lá. Uma
 * `Row` vinda do Supabase é atribuível a estes tipos.
 * ========================================================================== */

type Tabelas = Database['app']['Tables'];
type Insercao<N extends keyof Tabelas> = Tabelas[N]['Insert'];
type Obrigatorias<N extends keyof Tabelas, K extends keyof Tabelas[N]['Row']> =
  Pick<Tabelas[N]['Row'], K>;

/**
 * As colunas do cadastro que ficaram CONGELADAS em `app.empreendimento` desde a 0032 (sem
 * SELECT, INSERT nem UPDATE para o app; saem na 0033). Fora do tipo da linha de propósito: o
 * compilador recusa quem tentar ler ou escrever o cadastro no lugar antigo.
 */
export type ColunaCongeladaDoEmpreendimento =
  | 'programa' | 'rating' | 'vgv' | 'unidades' | 'cto_laudo' | 'desp_fin' | 'desp_com' | 'desp_seg'
  | 'desp_leg' | 'valor_terreno' | 'permutadas' | 'lgc_disponivel_override' | 'pj_limite';

/** Só a IDENTIDADE do empreendimento — o cadastro é de cada cenário (0032). */
export type LinhaEmpreendimento = Omit<Insercao<'empreendimento'>, ColunaCongeladaDoEmpreendimento>
  & Obrigatorias<'empreendimento', 'id' | 'nome' | 'extras'>;

export type LinhaCenario = Insercao<'cenario'> & Obrigatorias<'cenario',
  | 'id' | 'empresa_id' | 'empreendimento_id' | 'nome' | 'tem_fluxo' | 'linha_credito_id'
  | 'pct_fin' | 'unid_vend_dem' | 'unid_fin_dem' | 'aporte' | 'data_aporte'
  | 'terreno_garantia' | 'obra_gar_pct' | 'prazo_obra' | 'mes_inicial' | 'antecip_pct'
  | 'data_demanda_desejada' | 'data_contratacao' | 'lgc_pct' | 'lgc_limite_desejado'
  | 'pj_limitado_lgc' | 'min_obra_pct' | 'tem_evv' | 'funding_obra' | 'pls0_pct'
  | 'pls0_dias_aprov' | 'extensao_demanda' | 'custo_real_obra' | 'data_medicao_igh'
  | 'tempo_registro' | 'objetivo_guiado' | 'extras'
  /* o cadastro do cenário (0032) */
  | 'programa' | 'rating' | 'vgv' | 'unidades' | 'cto_laudo' | 'desp_fin' | 'desp_com' | 'desp_seg'
  | 'desp_leg' | 'valor_terreno' | 'permutadas' | 'lgc_disponivel' | 'pj_limite'>;

export type LinhaCenarioMes = Insercao<'cenario_mes'> & Obrigatorias<'cenario_mes',
  'cenario_id' | 'ordem' | 'ym' | 'vend_fin' | 'vend_nao' | 'obra_pct' | 'obra_custo_pct'>;

export type LinhaUnidade = Insercao<'unidade'> & Obrigatorias<'unidade',
  | 'id' | 'lado' | 'ordem' | 'nome' | 'valor_venda' | 'avaliacao' | 'data_venda'
  | 'data_registro' | 'financiada' | 'perc_fin' | 'permutada' | 'perm_inf_banco'
  | 'perm_como_venda' | 'fracao_ideal' | 'ingressa_demanda' | 'valor_solic_aval'
  | 'data_avaliacao' | 'forcar_avaliacao'>;

export type LinhaVaga = Insercao<'vaga'> & Obrigatorias<'vaga',
  | 'id' | 'lado' | 'ordem' | 'descricao' | 'tipo_id' | 'valor_venda' | 'avaliacao'
  | 'fracao' | 'permutada' | 'perm_inf_banco'>;

export type LinhaUnidadeVaga = Insercao<'unidade_vaga'> & Obrigatorias<'unidade_vaga',
  'lado' | 'unidade_id' | 'vaga_id'>;

export type LinhaObraServico = Insercao<'cenario_obra_servico'> &
  Obrigatorias<'cenario_obra_servico', 'servico_codigo' | 'pls_pct' | 'custo_pct'>;

export type LinhaObraMes = Insercao<'cenario_obra_mes'> &
  Obrigatorias<'cenario_obra_mes', 'servico_codigo' | 'offset_mes' | 'pct'>;

export type LinhaTrava = Insercao<'cenario_trava'> & Obrigatorias<'cenario_trava', 'campo'>;

/**
 * O cenário inteiro em forma de linhas — o que `paraBanco` devolve e `paraMotor` lê.
 * Os nomes dos campos são os das TABELAS (`unidade_vaga`, `cenario_obra_servico`,
 * `cenario_obra_mes`), para o repositório montar isto direto do `select('*')`.
 */
export interface LinhasBanco {
  empreendimento: LinhaEmpreendimento;
  cenario: LinhaCenario;
  meses: LinhaCenarioMes[];
  unidades: LinhaUnidade[];
  vagas: LinhaVaga[];
  unidadeVaga: LinhaUnidadeVaga[];
  obraServico: LinhaObraServico[];
  obraMes: LinhaObraMes[];
  travas: LinhaTrava[];
}

/** O par que o motor consome. */
export interface EntradaMotor {
  empreendimento: Empreendimento;
  lev: Lev;
}

/** Apelidos usados pelos módulos vizinhos (`repositorios.ts`, `rascunho.ts`). */
export type CenarioMotor = EntradaMotor;
export type LinhasCenario = LinhasBanco;

type Lado = Database['app']['Enums']['lado'];

/* ============================================================================
 * 2. Catálogos (transcritos de db/migrations/0003_catalogos.sql)
 * ========================================================================== */

/** `app.ref_linha_credito`. */
export const LINHAS_CREDITO: ReadonlyArray<{ id: number; nome: string }> = [
  { id: 1, nome: 'Apoio à Produção' },
];

/** `app.ref_vaga_tipo`. */
export const TIPOS_VAGA: ReadonlyArray<{ id: number; nome: string }> = [
  { id: 1, nome: 'Vaga de garagem' },
  { id: 2, nome: 'Depósito' },
];

/** `check (campo in (...))` de `app.cenario_trava`. NÃO são IDs `EL-NNN`. */
export const CAMPOS_TRAVA: readonly string[] = ['aporte', 'terreno', 'pctFin', 'lgc'];

/* ============================================================================
 * 3. Mapa de destino — a documentação que o compilador confere.
 *
 * `Record<keyof X, string>` exige TODA chave do tipo do motor: se `Lev`, `Empreendimento`,
 * `Unidade`, `Vaga` ou `Mes` ganhar um campo, o typecheck quebra aqui até alguém decidir
 * onde ele mora. É a trava contra "campo novo silenciosamente perdido".
 * ========================================================================== */

export const DESTINO_EMPREENDIMENTO: Readonly<Record<keyof Empreendimento, string>> = {
  id:            'app.empreendimento.id (ou cenario.extras.motor.emp.id se não for UUID)',
  nome:          'app.empreendimento.nome',
  programa:      'app.cenario.programa',
  rating:        'app.cenario.rating',
  VGV:           'app.cenario.vgv',
  unidades:      'app.cenario.unidades',
  ctoLaudo:      'app.cenario.cto_laudo',
  despFin:       'app.cenario.desp_fin',
  despCom:       'app.cenario.desp_com',
  despSeg:       'app.cenario.desp_seg',
  despLeg:       'app.cenario.desp_leg',
  valorTerreno:  'app.cenario.valor_terreno',
  lgcDisponivel: 'app.cenario.lgc_disponivel (o próprio valor, sem herança — 0032)',
  permutadas:    'app.cenario.permutadas',
  pjLimite:      'app.cenario.pj_limite (EL-192; NULL = chave ausente no motor)',
  unidadesDet:   'cenario.extras.motor.emp.unidadesDet (o motor não lê daqui — lê lev.unidadesDet)',
};

export const DESTINO_LEV: Readonly<Record<keyof Lev, string>> = {
  pctFin:               'app.cenario.pct_fin',
  pctFinPess:           'extras.motor.pctFinPess (SEM COLUNA)',
  unidVendDem:          'app.cenario.unid_vend_dem',
  unidFinDem:           'app.cenario.unid_fin_dem',
  aporte:               'app.cenario.aporte',
  terrenoGarantia:      'app.cenario.terreno_garantia',
  obraGarPct:           'app.cenario.obra_gar_pct',
  prazoObra:            'app.cenario.prazo_obra',
  antecipPct:           'app.cenario.antecip_pct',
  dataDemandaDesejada:  'app.cenario.data_demanda_desejada',
  lgcLimiteDesejado:    'app.cenario.lgc_limite_desejado',
  dataContratacao:      'app.cenario.data_contratacao',
  fundingObra:          'app.cenario.funding_obra',
  lgcPct:               'app.cenario.lgc_pct',
  minObraPct:           'app.cenario.min_obra_pct',
  temEVV:               'app.cenario.tem_evv',
  pls0Data:             'extras.motor.pls0Data (SEM COLUNA)',
  pls0Pct:              'app.cenario.pls0_pct',
  pls0DiasAprov:        'app.cenario.pls0_dias_aprov',
  dataAporte:           'app.cenario.data_aporte',
  extensaoDemanda:      'app.cenario.extensao_demanda',
  custoRealObra:        'app.cenario.custo_real_obra',
  dataMedicaoIGH:       'app.cenario.data_medicao_igh',
  tempoRegistro:        'app.cenario.tempo_registro',
  linhaCredito:         'app.cenario.linha_credito_id (via app.ref_linha_credito)',
  pjLimitadoLGC:        'app.cenario.pj_limitado_lgc',
  unidadesDet:          "app.unidade (lado='det')",
  unidadesDem:          "app.unidade (lado='dem')",
  vagasDet:             "app.vaga (lado='det')",
  vagasDem:             "app.vaga (lado='dem')",
  objetivoGuiado:       'app.cenario.objetivo_guiado',
  travadas:             'app.cenario_trava',
  mesInicial:           'app.cenario.mes_inicial (YYYY-MM ⇄ YYYY-MM-01)',
  meses:                'app.cenario_mes',
  obraServ:             'app.cenario_obra_servico + app.cenario_obra_mes',
  /* Fluxo pelo memorial (15/10/2026, migration 0034): os juros do saldo devedor do PJ. NULL = chave
     ausente no motor = juros não configurados (o padrão do pj_limite) */
  jurosIndexadorTaxa:   'app.cenario.juros_indexador_taxa (EL-206; NULL = chave ausente no motor)',
  jurosPctIndexador:    'app.cenario.juros_pct_indexador (EL-207; NULL = chave ausente no motor)',
  jurosTaxaNominalAa:   'app.cenario.juros_taxa_nominal_aa (EL-208; NULL = chave ausente no motor)',
  jurosIndexadorSaldo:  'app.cenario.juros_indexador_saldo (EL-209; NULL = chave ausente no motor)',
  /* 15/10/2026: amortização e carência do PJ (NULL = os padrões do memorial / sem carência) */
  amortInicioMeses:     'app.cenario.amort_inicio_meses (EL-210; NULL = chave ausente no motor)',
  amortParcelas:        'app.cenario.amort_parcelas (EL-211; NULL = chave ausente no motor)',
  carenciaCicloMeses:   'app.cenario.carencia_ciclo_meses (EL-212; NULL = chave ausente no motor)',
  carenciaAdicional1:   'app.cenario.carencia_adicional_1 (EL-213; NULL = chave ausente no motor)',
  carenciaAdicional2:   'app.cenario.carencia_adicional_2 (EL-214; NULL = chave ausente no motor)',
  carenciaAdicional3:   'app.cenario.carencia_adicional_3 (EL-215; NULL = chave ausente no motor)',
};

export const DESTINO_UNIDADE: Readonly<Record<keyof Unidade, string>> = {
  nome:                'app.unidade.nome (NULL = chave ausente no motor)',
  valorVenda:          'app.unidade.valor_venda',
  avaliacao:           'app.unidade.avaliacao',
  dataVenda:           'app.unidade.data_venda',
  dataRegistro:        "app.unidade.data_registro (só lado 'det')",
  financiada:          'app.unidade.financiada',
  percFin:             'app.unidade.perc_fin (0 = sentinela "usa lev.pctFin")',
  permutada:           'app.unidade.permutada',
  permInfBanco:        'app.unidade.perm_inf_banco',
  permComoVenda:       'app.unidade.perm_como_venda',
  m2:                  'app.unidade.fracao_ideal (metragem no det, fração no dem)',
  ingressaDemanda:     'app.unidade.ingressa_demanda',
  vagas:               'app.unidade_vaga',
  valorSolicitadoAval: 'app.unidade.valor_solic_aval',
  dataAvaliacao:       'app.unidade.data_avaliacao',
  forcarAvaliacao:     'app.unidade.forcar_avaliacao (EL-191; NULL = chave ausente no motor)',
};

export const DESTINO_VAGA: Readonly<Record<keyof Vaga, string>> = {
  id:           'app.vaga.id (token vg<N> em cenario.extras.motor.vagas)',
  desc:         'app.vaga.descricao',
  tipo:         'app.vaga.tipo_id (via app.ref_vaga_tipo)',
  valorVenda:   'app.vaga.valor_venda',
  avaliacao:    'app.vaga.avaliacao',
  fracao:       'app.vaga.fracao',
  permutada:    'app.vaga.permutada',
  permInfCaixa: 'app.vaga.perm_inf_banco (nome unificado; é "informada à Caixa")',
};

export const DESTINO_MES: Readonly<Record<keyof Mes, string>> = {
  ym:        'app.cenario_mes.ym',
  vendFin:   'app.cenario_mes.vend_fin',
  vendNao:   'app.cenario_mes.vend_nao',
  obra:      'app.cenario_mes.obra_pct',
  obraCusto: 'app.cenario_mes.obra_custo_pct',
};

/** Chaves de `Lev` que existem no banco como COLUNA/TABELA (o resto vai para extras). */
const CHAVES_LEV = Object.keys(DESTINO_LEV);
const CHAVES_EMPREENDIMENTO = Object.keys(DESTINO_EMPREENDIMENTO);

/* ============================================================================
 * 4. `extras.motor` — forma fechada e leitura defensiva
 * ========================================================================== */

/**
 * Espelho Json-compatível de `Unidade` (precisa ser `type`, não `interface`: só alias de
 * objeto ganha index signature implícita e vira atribuível a `Json`).
 */
type UnidadeJson = {
  nome?: string;
  valorVenda?: number;
  avaliacao?: number;
  dataVenda?: string;
  dataRegistro?: string;
  financiada?: boolean;
  percFin?: number;
  permutada?: boolean;
  permInfBanco?: boolean;
  permComoVenda?: boolean;
  m2?: number;
  ingressaDemanda?: boolean;
  vagas?: string[];
  valorSolicitadoAval?: number;
  dataAvaliacao?: string;
  forcarAvaliacao?: boolean;
};

/** Guarda de compilação: `UnidadeJson` e `Unidade` têm de continuar sendo o mesmo. */
type MesmoTipo<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
export const _UNIDADE_JSON_ESPELHA_UNIDADE: MesmoTipo<Unidade, UnidadeJson> = true;

/** A fidelidade de `b` — em `cenario.extras.motor.emp` desde a 0032. */
type ExtrasMotorEmp = {
  ausentes?: string[];
  nulos?: string[];
  id?: string;
  unidadesDet?: UnidadeJson[];
};

type ExtrasMotorCenario = {
  /** a fidelidade do cadastro (`b`) deste cenário — 0032 */
  emp?: ExtrasMotorEmp;
  ausentes?: string[];
  nulos?: string[];
  id?: string;
  pctFinPess?: number;
  pls0Data?: string;
  /** só quando o nome não está em `app.ref_linha_credito` */
  linhaCredito?: string;
  /** só quando `lev.travadas` tem valor fora do CHECK de `app.cenario_trava` */
  travadas?: string[];
  /** por ID de linha de `app.vaga`: token do motor e tipo fora do catálogo */
  vagas?: { [idLinha: string]: { id?: string; tipo?: string } };
};

/** Chaves aceitas em `extras.motor` — o teste garante que nada além disto é escrito.
 *  `app.empreendimento.extras`: NADA desde a 0032 (o mapeamento não escreve mais lá). */
export const CHAVES_EXTRAS_EMPREENDIMENTO: readonly string[] = [];
export const CHAVES_EXTRAS_CENARIO: readonly string[] =
  ['ausentes', 'nulos', 'id', 'pctFinPess', 'pls0Data', 'linhaCredito', 'travadas', 'vagas', 'emp'];
/** Chaves aceitas em `cenario.extras.motor.emp` (a fidelidade de `b`). */
export const CHAVES_EXTRAS_EMP: readonly string[] = ['ausentes', 'nulos', 'id', 'unidadesDet'];

const ehObjeto = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** Lê `extras.motor` de uma linha do banco sem confiar no conteúdo (é `jsonb` livre). */
function lerExtrasMotor(extras: Json | undefined): Record<string, unknown> {
  if (!ehObjeto(extras)) return {};
  const motor = extras['motor'];
  return ehObjeto(motor) ? motor : {};
}

const listaDeTexto = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

const textoOuNulo = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const numeroOuNulo = (v: unknown): number | null =>
  (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** EL-192: o "PJ limite" só existe quando é número finito > 0 — a mesma régua do motor
 *  (`tetoInformadoPJ`). Todo o resto é "sem limite", e no banco isso é NULL. */
const pjLimiteParaBanco = (v: unknown): number | null =>
  (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null);

/** 0034 (Fluxo pelo memorial): só os valores que o motor conhece vão para o banco; o resto é NULL
 *  (= juros não configurados). Sem CHECK no banco — quem normaliza é aqui. */
const jurosTaxaParaBanco = (v: unknown): string | null =>
  (v === 'cdi' || v === 'poupanca' || v === 'nenhum' ? v : null);
const jurosSaldoParaBanco = (v: unknown): string | null => (v === 'tr' || v === 'nenhum' ? v : null);
/* revisão do PR SAAS-984, comentário 6 (20/10/2026): os parâmetros do fluxo chegam ao `salvar_cenario` dentro do intervalo
   da coluna — meses e parcelas em smallint, percentuais em numeric(12,6). Um valor fora derrubaria o `jsonb_populate_record`
   e, com ele, o salvamento do cenário INTEIRO (uma transação só). Meses e parcelas: inteiros em [0, 600] (o horizonte da
   tabela; parcelas ≥ 1); percentuais: |x| ≤ 999.999 */
const LIMITE_MESES = 600, LIMITE_NUMERIC_12_6 = 999_999;
const mesesParaBanco = (v: unknown, minimo = 0): number | null => {
  const n = numeroOuNulo(v);
  return n == null ? null : Math.min(LIMITE_MESES, Math.max(minimo, Math.round(n)));
};
const percentualParaBanco = (v: unknown): number | null => {
  const n = numeroOuNulo(v);
  return n == null ? null : Math.min(LIMITE_NUMERIC_12_6, Math.max(-LIMITE_NUMERIC_12_6, n));
};

/* ============================================================================
 * 5. Utilidades
 * ========================================================================== */

const RX_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Tenant de fallback. O chamador real DEVE passar `opcoes.empresaId`. */
export const EMPRESA_NAO_INFORMADA = '00000000-0000-0000-0000-000000000000';

/** Objeto do motor lido como saco de chaves — o golden traz `null` onde o tipo diz `number`. */
type Bruto = Record<string, unknown>;
const bruto = (o: object): Bruto => o as unknown as Bruto;

/**
 * Data ISO do motor → coluna `date`. Vai LITERAL (sem `Date`, sem fuso).
 * `''` e ausente viram `NULL`; a diferença entre os dois fica em `ausentes`/`nulos`.
 */
const dataParaBanco = (v: unknown): string | null =>
  (typeof v === 'string' && v !== '' ? v : null);

/** Coluna `date` → data ISO do motor. `NULL` → `''` (é o vazio do protótipo). */
const dataParaMotor = (v: string | null | undefined): string => (typeof v === 'string' ? v : '');

/* ============================================================================
 * 6. MOTOR → BANCO
 * ========================================================================== */

export interface OpcoesParaBanco {
  /** tenant dono das linhas (`empresa_id` é `NOT NULL` em todas as tabelas). */
  empresaId?: string;
  /** id da linha de `app.cenario`; por padrão é gerado. */
  cenarioId?: string;
  /** `app.cenario.nome` — não existe em `Lev` (mora em `Cenario`). */
  nomeCenario?: string;
  /** `app.cenario.tem_fluxo` — não existe em `Lev` (mora em `Cenario`). */
  temFluxo?: boolean;
  /** exigidos pelo CHECK `cen_extensao_autor` quando `extensaoDemanda > 0`. */
  extensaoAutorizadaPor?: string | null;
  extensaoAutorizadaEm?: string | null;
  /** gerador de UUID; injetável para deixar a saída determinística em teste. */
  novoId?: () => string;
}

/**
 * Classificador de chave: separa ausente (`undefined`/não existe) de nula (`null`) de
 * com-valor, acumulando as duas primeiras para `extras.motor`.
 *
 * `undefined` conta como AUSENTE de propósito: JSON não distingue os dois e o protótipo
 * nunca grava `undefined` explícito.
 */
function classificador(fonte: object) {
  const o = bruto(fonte);
  const ausentes: string[] = [];
  const nulos: string[] = [];

  function cru(k: string): unknown {
    if (!(k in o) || o[k] === undefined) { ausentes.push(k); return undefined; }
    if (o[k] === null) { nulos.push(k); return undefined; }
    return o[k];
  }
  return {
    ausentes,
    nulos,
    cru,
    num: (k: string, padrao = 0): number => {
      const v = cru(k);
      return typeof v === 'number' && Number.isFinite(v) ? v : padrao;
    },
    txt: (k: string, padrao = ''): string => {
      const v = cru(k);
      return typeof v === 'string' ? v : padrao;
    },
    bool: (k: string, padrao = false): boolean => {
      const v = cru(k);
      return typeof v === 'boolean' ? v : padrao;
    },
    data: (k: string): string | null => dataParaBanco(cru(k)),
  };
}

/**
 * Identidade de linha. Se o id do motor já é UUID (caminho do produto) ele VIRA o id da
 * linha e nada vai para `extras`. Se não é — ou se já foi usado neste cenário, que é o
 * caso do `vg1` duplicado entre `det` e `dem` pelo `espelharDados` — a linha ganha UUID
 * novo e o token original é devolvido para ser guardado.
 */
function identidade(idMotor: unknown, usados: Set<string>, idUnico: () => string):
  { id: string; token: string | null } {
  if (typeof idMotor === 'string' && RX_UUID.test(idMotor) && !usados.has(idMotor)) {
    usados.add(idMotor);
    return { id: idMotor, token: null };
  }
  return { id: idUnico(), token: typeof idMotor === 'string' ? idMotor : null };
}

/** Tira o que não carrega informação: `undefined`, lista vazia e objeto vazio. */
function semVazios(o: Record<string, unknown>): Record<string, unknown> {
  const limpo: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) {
    if (v === undefined) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    if (ehObjeto(v) && Object.keys(v).length === 0) continue;
    limpo[k] = v;
  }
  return limpo;
}

function montarExtras(motor: Record<string, unknown>): Json {
  const limpo = semVazios(motor);
  return (Object.keys(limpo).length ? { motor: limpo } : {}) as Json;
}

/** Converte `{empreendimento, lev}` do motor nas linhas das 9 tabelas. */
export function paraBanco(entrada: EntradaMotor, opcoes: OpcoesParaBanco = {}): LinhasBanco {
  const novoId = opcoes.novoId ?? (() => globalThis.crypto.randomUUID());
  const empresa_id = opcoes.empresaId ?? EMPRESA_NAO_INFORMADA;
  const usados = new Set<string>();
  const idUnico = (): string => {
    let id = novoId();
    while (usados.has(id)) id = novoId();
    usados.add(id);
    return id;
  };

  /* ---- app.empreendimento: só a IDENTIDADE (0032) ------------------------- */
  const b = entrada.empreendimento;
  const cb = classificador(b);
  const idEmp = identidade(cb.cru('id'), usados, idUnico);

  const programa = cb.txt('programa', 'SBPE') as LinhaCenario['programa'];
  const rating = cb.txt('rating', 'A') as LinhaCenario['rating'];
  const unidadesDetB = cb.cru('unidadesDet');

  const extrasEmp: ExtrasMotorEmp = {};
  if (idEmp.token !== null) extrasEmp.id = idEmp.token;
  if (Array.isArray(unidadesDetB)) extrasEmp.unidadesDet = unidadesDetB as UnidadeJson[];

  /* O `extras` do empreendimento NÃO é escrito: a fidelidade de `b` vai com o cadastro, no
     cenário (`extras.motor.emp`). O do banco ficou congelado e ninguém o lê. */
  const empreendimento: LinhaEmpreendimento = {
    id: idEmp.id,
    empresa_id,
    nome: cb.txt('nome'),
    extras: {},
  };

  /* ---- o cadastro (`b`) → colunas de app.cenario (0032) ------------------ */
  const cadastro = {
    programa,
    rating,
    vgv: cb.num('VGV'),
    unidades: cb.num('unidades'),
    cto_laudo: cb.num('ctoLaudo'),
    desp_fin: cb.num('despFin'),
    desp_com: cb.num('despCom'),
    desp_seg: cb.num('despSeg'),
    desp_leg: cb.num('despLeg'),
    valor_terreno: cb.num('valorTerreno'),
    permutadas: cb.num('permutadas'),
    /* O próprio valor do cenário — não há mais "override × LGC da incorporadora". Ausente ou
       `null` vira 0 na coluna (NOT NULL), e a ausência/nulidade fica em `emp`. */
    lgc_disponivel: cb.num('lgcDisponivel'),
    /* EL-192 (09/10/2026). Lido FORA do classificador, de propósito: pelo `cb.cru` a chave
       ausente entraria em `ausentes` de TODO cenário (inclusive dos goldens) — sujeira sem
       informação, porque NULL já diz "ausente".
       ⚠ `null` EXPLÍCITO, nunca `undefined`: no contrato do `salvar_cenario`, chave ausente
       MANTÉM o valor do banco — apagar o campo na tela não surtiria efeito, sem erro nenhum.
       ≤ 0 (e não-número) vira NULL: zero é "sem limite". */
    pj_limite: pjLimiteParaBanco(bruto(b)['pjLimite']),
  };
  const fidelidadeEmp = semVazios({
    ...extrasEmp,
    ausentes: cb.ausentes.filter(k => CHAVES_EMPREENDIMENTO.includes(k)),
    nulos: cb.nulos.filter(k => CHAVES_EMPREENDIMENTO.includes(k)),
  }) as ExtrasMotorEmp;

  /* ---- app.cenario ------------------------------------------------------- */
  const L = entrada.lev;
  const cl = classificador(L);
  const cenario_id = opcoes.cenarioId ?? idUnico();

  const nomeLinha = cl.txt('linhaCredito');
  const refLinha = LINHAS_CREDITO.find(x => x.nome === nomeLinha);
  const extensao = cl.num('extensaoDemanda');

  const extrasCen: ExtrasMotorCenario = {};
  const pctFinPess = cl.cru('pctFinPess');
  if (typeof pctFinPess === 'number') extrasCen.pctFinPess = pctFinPess;
  const pls0Data = cl.cru('pls0Data');
  if (typeof pls0Data === 'string') extrasCen.pls0Data = pls0Data;
  if (nomeLinha !== '' && !refLinha) extrasCen.linhaCredito = nomeLinha;

  /* ---- app.cenario_mes --------------------------------------------------- */
  const mesesMotor = cl.cru('meses');
  const meses: LinhaCenarioMes[] = (Array.isArray(mesesMotor) ? mesesMotor : []).map((m, i) => {
    const cm = classificador(ehObjeto(m) ? m : {});
    return {
      empresa_id, cenario_id, id: idUnico(), ordem: i,
      ym: cm.num('ym'),
      vend_fin: cm.num('vendFin'),
      vend_nao: cm.num('vendNao'),
      obra_pct: cm.num('obra'),
      obra_custo_pct: cm.num('obraCusto'),
    };
  });

  /* ---- app.vaga (antes das unidades: o vínculo precisa do id da vaga) ---- */
  const vagas: LinhaVaga[] = [];
  const idPorToken = new Map<string, string>();   // `${lado} ${token}` → id da linha
  const extrasVagas: NonNullable<ExtrasMotorCenario['vagas']> = {};

  for (const lado of ['det', 'dem'] as const) {
    const arr = cl.cru(lado === 'det' ? 'vagasDet' : 'vagasDem');
    if (!Array.isArray(arr)) continue;
    arr.forEach((v, i) => {
      const cv = classificador(ehObjeto(v) ? v : {});
      const idv = identidade(cv.cru('id'), usados, idUnico);
      const nomeTipo = cv.txt('tipo');
      const refTipo = TIPOS_VAGA.find(x => x.nome === nomeTipo);
      const residuo: { id?: string; tipo?: string } = {};
      if (idv.token !== null) residuo.id = idv.token;
      if (nomeTipo !== '' && !refTipo) residuo.tipo = nomeTipo;
      if (Object.keys(residuo).length) extrasVagas[idv.id] = residuo;
      if (idv.token !== null) idPorToken.set(`${lado} ${idv.token}`, idv.id);
      else idPorToken.set(`${lado} ${idv.id}`, idv.id);

      vagas.push({
        empresa_id, cenario_id, id: idv.id, lado, ordem: i,
        descricao: cv.txt('desc'),
        tipo_id: refTipo ? refTipo.id : (TIPOS_VAGA[0]?.id ?? 1),
        valor_venda: cv.num('valorVenda'),
        avaliacao: cv.num('avaliacao'),
        fracao: cv.num('fracao'),
        permutada: cv.bool('permutada'),
        perm_inf_banco: cv.bool('permInfCaixa'),
      });
    });
  }
  if (Object.keys(extrasVagas).length) extrasCen.vagas = extrasVagas;

  /* ---- app.unidade + app.unidade_vaga ------------------------------------ */
  const unidades: LinhaUnidade[] = [];
  const unidadeVaga: LinhaUnidadeVaga[] = [];

  for (const lado of ['det', 'dem'] as const) {
    const arr = cl.cru(lado === 'det' ? 'unidadesDet' : 'unidadesDem');
    if (!Array.isArray(arr)) continue;
    arr.forEach((u, i) => {
      /* A5: `app.unidade` não tem `extras`, então o desvio de chave POR LINHA (unidade
         sem `vagas`, por exemplo) não teria onde ser registrado — `cu.ausentes`/`cu.nulos`
         são descartados de propósito. O conjunto canônico de chaves é o da fábrica do v67
         (`novaUnidade`, v67 linha 3305) e o teste prova que os 64 goldens o respeitam. */
      const cu = classificador(ehObjeto(u) ? u : {});
      const id = idUnico();
      const nome = cu.cru('nome');
      const solicAval = cu.cru('valorSolicitadoAval');
      const dataAval = cu.cru('dataAvaliacao');
      /* EL-191 (08/10/2026): a escolha "Avaliação (forçada)". Coluna NULA = chave ausente,
         como `nome` e `valorSolicitadoAval` — é o que mantém a ida e volta idêntica nos
         goldens, que não têm a chave. `false` explícito é preservado como `false`. */
      const forcar = cu.cru('forcarAvaliacao');

      unidades.push({
        empresa_id, cenario_id, id, lado, ordem: i,
        nome: typeof nome === 'string' ? nome : null,
        valor_venda: cu.num('valorVenda'),
        avaliacao: cu.num('avaliacao'),
        data_venda: cu.data('dataVenda'),
        /* CHECK un_data_registro_lado: a coluna só aceita valor no lado det. */
        data_registro: lado === 'det' ? cu.data('dataRegistro') : null,
        financiada: cu.bool('financiada', lado === 'det'),
        perc_fin: cu.num('percFin'),
        permutada: cu.bool('permutada'),
        perm_inf_banco: cu.bool('permInfBanco'),
        perm_como_venda: cu.bool('permComoVenda'),
        /* semântica dupla preservada: metragem no det, fração ideal no dem. */
        fracao_ideal: cu.num('m2'),
        ingressa_demanda: cu.bool('ingressaDemanda'),
        valor_solic_aval: numeroOuNulo(solicAval),
        data_avaliacao: dataParaBanco(dataAval),
        forcar_avaliacao: typeof forcar === 'boolean' ? forcar : null,
      });

      const vinculos = cu.cru('vagas');
      if (Array.isArray(vinculos)) {
        for (const token of vinculos) {
          if (typeof token !== 'string') continue;
          const vaga_id = idPorToken.get(`${lado} ${token}`);
          if (vaga_id === undefined) continue;   // token sem vaga: o v67 também ignora
          unidadeVaga.push({ empresa_id, cenario_id, lado, unidade_id: id, vaga_id });
        }
      }
    });
  }

  /* ---- app.cenario_obra_servico + app.cenario_obra_mes ------------------- */
  const obraServico: LinhaObraServico[] = [];
  const obraMes: LinhaObraMes[] = [];
  const obra = cl.cru('obraServ');
  if (ehObjeto(obra)) {
    for (const [codigo, servico] of Object.entries(obra)) {
      const cs = classificador(ehObjeto(servico) ? servico : {});
      obraServico.push({
        empresa_id, cenario_id, servico_codigo: codigo,
        pls_pct: cs.num('pls'),
        custo_pct: cs.num('custo'),
      });
      const m = cs.cru('m');
      /* Uma linha por índice, INCLUSIVE os zeros: é o que devolve `m.length` exato na
         volta (o trigger `poda_obra_mes` já assume um mês por coluna do array). */
      if (Array.isArray(m)) {
        m.forEach((pct, offset) => obraMes.push({
          empresa_id, cenario_id, servico_codigo: codigo, offset_mes: offset,
          pct: typeof pct === 'number' && Number.isFinite(pct) ? pct : 0,
        }));
      }
    }
  }

  /* ---- app.cenario_trava ------------------------------------------------- */
  const travadas = cl.cru('travadas');
  const travas: LinhaTrava[] = [];
  if (Array.isArray(travadas)) {
    const fora = travadas.filter(t => typeof t !== 'string' || !CAMPOS_TRAVA.includes(t));
    /* valor fora do CHECK não vira linha: guarda-se a lista original inteira, que é o
       único jeito de devolver a ordem e o valor exatos. */
    if (fora.length) extrasCen.travadas = travadas.map(t => String(t));
    for (const t of travadas) {
      if (typeof t === 'string' && CAMPOS_TRAVA.includes(t)) {
        travas.push({ empresa_id, cenario_id, campo: t });
      }
    }
  }

  const cenario: LinhaCenario = {
    id: cenario_id,
    empresa_id,
    empreendimento_id: idEmp.id,
    nome: opcoes.nomeCenario ?? 'Cenário',
    tem_fluxo: opcoes.temFluxo ?? true,
    linha_credito_id: refLinha ? refLinha.id : (LINHAS_CREDITO[0]?.id ?? 1),
    pct_fin: cl.num('pctFin'),
    unid_vend_dem: cl.num('unidVendDem'),
    unid_fin_dem: cl.num('unidFinDem'),
    aporte: cl.num('aporte'),
    data_aporte: cl.data('dataAporte'),
    terreno_garantia: cl.bool('terrenoGarantia'),
    obra_gar_pct: cl.num('obraGarPct'),
    prazo_obra: cl.num('prazoObra', 18),
    mes_inicial: mesInicialParaBanco(cl.cru('mesInicial')),
    antecip_pct: cl.num('antecipPct'),
    data_demanda_desejada: cl.data('dataDemandaDesejada'),
    data_contratacao: cl.data('dataContratacao'),
    lgc_pct: cl.num('lgcPct'),
    lgc_limite_desejado: cl.num('lgcLimiteDesejado'),
    pj_limitado_lgc: cl.bool('pjLimitadoLGC'),
    min_obra_pct: cl.num('minObraPct'),
    tem_evv: cl.bool('temEVV'),
    funding_obra: cl.txt('fundingObra', 'SBPE') as LinhaCenario['funding_obra'],
    pls0_pct: cl.num('pls0Pct'),
    pls0_dias_aprov: cl.num('pls0DiasAprov', 30),
    extensao_demanda: extensao,
    /* Contrato do RPC (0010_rpc.sql:100, via jsonb_populate_record): chave AUSENTE
       preserva o valor no banco, `null` explícito ZERA.
       Emitir `null` quando não se está autorizando apagava o par autor/data de um
       cenário JÁ autorizado, e então o CHECK `cen_extensao_autor` recusava a
       gravação: nenhum analista conseguia salvar um cenário com extensão, e o
       gestor só conseguia reautorizando (reescrevendo a data real).
       Também não fabricamos mais a data com `new Date()`: data de autorização é
       fato jurídico, não valor calculado no cliente.
       `undefined` some do JSON e é exatamente a "chave ausente" que preserva. */
    extensao_autorizada_por: extensao > 0 ? (opcoes.extensaoAutorizadaPor ?? undefined) : null,
    extensao_autorizada_em: extensao > 0 ? (opcoes.extensaoAutorizadaEm ?? undefined) : null,
    custo_real_obra: cl.num('custoRealObra'),
    data_medicao_igh: cl.data('dataMedicaoIGH'),
    tempo_registro: cl.num('tempoRegistro', 60),
    /* 0034: os juros — direto do lev (não passam pelo classificador: NULL já é "ausente", como o pj_limite) */
    juros_indexador_taxa: jurosTaxaParaBanco(bruto(L)['jurosIndexadorTaxa']),
    juros_pct_indexador: percentualParaBanco(bruto(L)['jurosPctIndexador']),
    juros_taxa_nominal_aa: percentualParaBanco(bruto(L)['jurosTaxaNominalAa']),
    juros_indexador_saldo: jurosSaldoParaBanco(bruto(L)['jurosIndexadorSaldo']),
    /* 15/10/2026: amortização e carência (mesma regra: NULL = chave ausente) */
    amort_inicio_meses: mesesParaBanco(bruto(L)['amortInicioMeses']),
    amort_parcelas: mesesParaBanco(bruto(L)['amortParcelas'], 1),
    carencia_ciclo_meses: mesesParaBanco(bruto(L)['carenciaCicloMeses']),
    carencia_adicional_1: percentualParaBanco(bruto(L)['carenciaAdicional1']),
    carencia_adicional_2: percentualParaBanco(bruto(L)['carenciaAdicional2']),
    carencia_adicional_3: percentualParaBanco(bruto(L)['carenciaAdicional3']),
    objetivo_guiado: cl.txt('objetivoGuiado'),
    ...cadastro,
    extras: montarExtras({
      ...extrasCen,
      ausentes: cl.ausentes.filter(k => CHAVES_LEV.includes(k)),
      nulos: cl.nulos.filter(k => CHAVES_LEV.includes(k)),
      emp: fidelidadeEmp,
    }),
  };

  return {
    empreendimento, cenario, meses, unidades, vagas, unidadeVaga,
    obraServico, obraMes, travas,
  };
}

/** `'YYYY-MM'` (input type=month) → `'YYYY-MM-01'` (CHECK `cen_mes_inicial_dia1`). */
function mesInicialParaBanco(v: unknown): string {
  if (typeof v !== 'string' || v.length < 7) return '1970-01-01';
  return `${v.slice(0, 7)}-01`;
}

/* ============================================================================
 * 7. BANCO → MOTOR
 * ========================================================================== */

/** Converte as linhas das 9 tabelas de volta em `{empreendimento, lev}`. */
export function paraMotor(linhas: LinhasBanco): EntradaMotor {
  return {
    empreendimento: montarEmpreendimento(linhas.empreendimento, linhas.cenario),
    lev: montarLev(linhas),
  };
}

/**
 * O `b` do motor: do EMPREENDIMENTO só a identidade (id e nome); o cadastro e a fidelidade dele
 * vêm do CENÁRIO (0032). O `extras` do empreendimento é ignorado de propósito — ficou congelado,
 * e um `nulos: ['VGV']` velho dali esconderia o VGV que o cenário publicou.
 */
function montarEmpreendimento(emp: LinhaEmpreendimento, linha: LinhaCenario): Empreendimento {
  const exCen = lerExtrasMotor(linha.extras);
  const ex: Record<string, unknown> = ehObjeto(exCen['emp']) ? exCen['emp'] : {};
  const ausentes = new Set(listaDeTexto(ex['ausentes']));
  const nulos = new Set(listaDeTexto(ex['nulos']));

  /* O protótipo grava `null` em campo declarado `number` (o motor lê com `||0`) — ver o
     caso S20b do golden. Montamos como saco de chaves e convertemos no fim: o mapeamento
     devolve EXATAMENTE o que entrou. */
  const b: Bruto = {};
  const por = (chave: string, valor: unknown): void => {
    if (ausentes.has(chave)) return;
    b[chave] = nulos.has(chave) ? null : valor;
  };

  por('id', textoOuNulo(ex['id']) ?? emp.id);
  por('nome', emp.nome);
  por('programa', linha.programa);
  por('rating', linha.rating);
  por('VGV', linha.vgv);
  por('unidades', linha.unidades);
  por('ctoLaudo', linha.cto_laudo);
  por('despFin', linha.desp_fin);
  por('despCom', linha.desp_com);
  por('despSeg', linha.desp_seg);
  por('despLeg', linha.desp_leg);
  por('valorTerreno', linha.valor_terreno);
  por('lgcDisponivel', linha.lgc_disponivel);
  por('permutadas', linha.permutadas);
  /* EL-192: NULL = chave AUSENTE (não `null`, não 0) — é o que mantém a ida e volta dos
     goldens idêntica, e o motor lê ausente como "sem limite". Fora do `por` porque a chave
     nunca vai para `ausentes`/`nulos` (ver `paraBanco`). */
  if (linha.pj_limite !== null && linha.pj_limite !== undefined) b['pjLimite'] = linha.pj_limite;
  /* `montarExtras` não guarda array vazio, então "não está em extras" = `[]`. Quem diz
     que a chave não existia é `ausentes` (checado dentro de `por`). */
  por('unidadesDet', Array.isArray(ex['unidadesDet']) ? ex['unidadesDet'] : []);

  return b as unknown as Empreendimento;
}

function montarLev(linhas: LinhasBanco): Lev {
  const c = linhas.cenario;
  const ex = lerExtrasMotor(c.extras);
  const ausentes = new Set(listaDeTexto(ex['ausentes']));
  const nulos = new Set(listaDeTexto(ex['nulos']));

  const L: Bruto = {};
  const por = (chave: string, valor: unknown): void => {
    if (ausentes.has(chave)) return;
    L[chave] = nulos.has(chave) ? null : valor;
  };

  /* ---- vagas (antes das unidades: o vínculo devolve o token da vaga) ----- */
  const extrasVagas = ehObjeto(ex['vagas']) ? ex['vagas'] : {};
  const tokenPorLinha = new Map<string, string>();
  const ordemPorLinha = new Map<string, number>();
  const vagasPorLado: Record<Lado, Vaga[]> = { det: [], dem: [] };

  for (const v of ordenar(linhas.vagas)) {
    const residuo = ehObjeto(extrasVagas[v.id]) ? extrasVagas[v.id] as Bruto : {};
    const token = textoOuNulo(residuo['id']) ?? v.id;
    tokenPorLinha.set(v.id, token);
    ordemPorLinha.set(v.id, v.ordem);
    const tipoRef = TIPOS_VAGA.find(x => x.id === v.tipo_id);
    vagasPorLado[v.lado].push({
      id: token,
      desc: v.descricao,
      tipo: textoOuNulo(residuo['tipo']) ?? (tipoRef ? tipoRef.nome : ''),
      valorVenda: v.valor_venda,
      avaliacao: v.avaliacao,
      fracao: v.fracao,
      permutada: v.permutada,
      permInfCaixa: v.perm_inf_banco,
    });
  }

  /* ---- unidades + vínculos ---------------------------------------------- */
  const vinculosPorUnidade = new Map<string, string[]>();
  for (const uv of linhas.unidadeVaga) {
    const lista = vinculosPorUnidade.get(uv.unidade_id) ?? [];
    lista.push(uv.vaga_id);
    vinculosPorUnidade.set(uv.unidade_id, lista);
  }

  const unidadesPorLado: Record<Lado, Unidade[]> = { det: [], dem: [] };
  for (const u of ordenar(linhas.unidades)) {
    const vinculos = (vinculosPorUnidade.get(u.id) ?? [])
      .slice()
      .sort((a, b) => (ordemPorLinha.get(a) ?? 0) - (ordemPorLinha.get(b) ?? 0))
      .map(id => tokenPorLinha.get(id) ?? id);

    const unidade: Bruto = {
      valorVenda: u.valor_venda,
      avaliacao: u.avaliacao,
      dataVenda: dataParaMotor(u.data_venda),
      financiada: u.financiada,
      percFin: u.perc_fin,
      permutada: u.permutada,
      permInfBanco: u.perm_inf_banco,
      permComoVenda: u.perm_como_venda,
      m2: u.fracao_ideal,
      ingressaDemanda: u.ingressa_demanda,
      vagas: vinculos,
    };
    /* chaves que a fábrica do v67 só cria em situação específica — a coluna nula é o
       registro de que a chave não existia (ver A2/A3 no cabeçalho). */
    if (u.nome !== null) unidade['nome'] = u.nome;
    if (u.lado === 'det') unidade['dataRegistro'] = dataParaMotor(u.data_registro);
    if (u.valor_solic_aval !== null) unidade['valorSolicitadoAval'] = u.valor_solic_aval;
    if (u.data_avaliacao !== null) unidade['dataAvaliacao'] = u.data_avaliacao;
    if (u.forcar_avaliacao !== null && u.forcar_avaliacao !== undefined) unidade['forcarAvaliacao'] = u.forcar_avaliacao;

    unidadesPorLado[u.lado].push(unidade as unknown as Unidade);
  }

  /* ---- meses ------------------------------------------------------------- */
  const meses: Mes[] = ordenar(linhas.meses).map(m => ({
    ym: m.ym,
    vendFin: m.vend_fin,
    vendNao: m.vend_nao,
    obra: m.obra_pct,
    obraCusto: m.obra_custo_pct,
  }));

  /* ---- obra -------------------------------------------------------------- */
  const obraServ: Record<string, { pls: number; custo: number; m: number[] }> = {};
  for (const s of linhas.obraServico) {
    obraServ[s.servico_codigo] = { pls: s.pls_pct, custo: s.custo_pct, m: [] };
  }
  for (const r of linhas.obraMes) {
    const alvo = obraServ[r.servico_codigo];
    if (!alvo) continue;
    while (alvo.m.length <= r.offset_mes) alvo.m.push(0);
    alvo.m[r.offset_mes] = r.pct;
  }

  /* ---- travas ------------------------------------------------------------ */
  const travadas = 'travadas' in ex
    ? listaDeTexto(ex['travadas'])
    : linhas.travas.map(t => t.campo);

  /* ---- colunas ----------------------------------------------------------- */
  const refLinha = LINHAS_CREDITO.find(x => x.id === c.linha_credito_id);

  por('pctFin', c.pct_fin);
  por('pctFinPess', numeroOuNulo(ex['pctFinPess']) ?? 0);
  por('unidVendDem', c.unid_vend_dem);
  por('unidFinDem', c.unid_fin_dem);
  por('aporte', c.aporte);
  por('terrenoGarantia', c.terreno_garantia);
  por('obraGarPct', c.obra_gar_pct);
  por('prazoObra', c.prazo_obra);
  por('antecipPct', c.antecip_pct);
  por('dataDemandaDesejada', dataParaMotor(c.data_demanda_desejada));
  por('lgcLimiteDesejado', c.lgc_limite_desejado);
  por('dataContratacao', dataParaMotor(c.data_contratacao));
  por('fundingObra', c.funding_obra);
  por('lgcPct', c.lgc_pct);
  por('minObraPct', c.min_obra_pct);
  por('temEVV', c.tem_evv);
  por('pls0Data', textoOuNulo(ex['pls0Data']) ?? '');
  por('pls0Pct', c.pls0_pct);
  por('pls0DiasAprov', c.pls0_dias_aprov);
  por('dataAporte', dataParaMotor(c.data_aporte));
  por('extensaoDemanda', c.extensao_demanda);
  por('custoRealObra', c.custo_real_obra);
  por('dataMedicaoIGH', dataParaMotor(c.data_medicao_igh));
  por('tempoRegistro', c.tempo_registro);
  /* 0034: NULL = chave ausente (juros não configurados) — o banco antes da 0034 nem tem as colunas */
  if (c.juros_indexador_taxa != null) L['jurosIndexadorTaxa'] = c.juros_indexador_taxa;
  if (c.juros_pct_indexador != null) L['jurosPctIndexador'] = c.juros_pct_indexador;
  if (c.juros_taxa_nominal_aa != null) L['jurosTaxaNominalAa'] = c.juros_taxa_nominal_aa;
  if (c.juros_indexador_saldo != null) L['jurosIndexadorSaldo'] = c.juros_indexador_saldo;
  if (c.amort_inicio_meses != null) L['amortInicioMeses'] = c.amort_inicio_meses;
  if (c.amort_parcelas != null) L['amortParcelas'] = c.amort_parcelas;
  if (c.carencia_ciclo_meses != null) L['carenciaCicloMeses'] = c.carencia_ciclo_meses;
  if (c.carencia_adicional_1 != null) L['carenciaAdicional1'] = c.carencia_adicional_1;
  if (c.carencia_adicional_2 != null) L['carenciaAdicional2'] = c.carencia_adicional_2;
  if (c.carencia_adicional_3 != null) L['carenciaAdicional3'] = c.carencia_adicional_3;
  por('linhaCredito', textoOuNulo(ex['linhaCredito']) ?? (refLinha ? refLinha.nome : ''));
  por('pjLimitadoLGC', c.pj_limitado_lgc);
  por('unidadesDet', unidadesPorLado.det);
  por('unidadesDem', unidadesPorLado.dem);
  por('vagasDet', vagasPorLado.det);
  por('vagasDem', vagasPorLado.dem);
  por('objetivoGuiado', c.objetivo_guiado);
  por('travadas', travadas);
  por('mesInicial', c.mes_inicial.slice(0, 7));
  por('meses', meses);
  por('obraServ', obraServ);

  return L as unknown as Lev;
}

/** Ordena por `ordem` sem mexer no array recebido (a consulta pode vir em qualquer ordem). */
function ordenar<T extends { ordem: number }>(linhas: readonly T[]): T[] {
  return linhas.slice().sort((a, b) => a.ordem - b.ordem);
}
